import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type InfiniteData,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  ActivityEventView,
  CaseCounts,
  CaseDetail,
  CaseListItem,
  CursorPage,
  MarkReadInput,
  SearchHit,
} from "../../shared/contracts.js";
import type { CaseListView } from "../../shared/domain.js";
import { request } from "./http.js";

/**
 * Case read queries: the cursor-paginated list for one view, the centre-list
 * counts, the global search and the case detail (spec §10–§13), as React
 * Query hooks shared by the Parts desktop workspace (Task 10) and the shared
 * Surface workspace (Task 13).
 *
 * The client asks and displays; it never derives. Which cases belong to a
 * view, whether an Arrived case is still inside its 48 hours, what the status
 * is, what the person may do and whether a case is unread are all answered by
 * the server (`GET /api/cases`, `GET /api/cases/counts`, `GET /api/search`,
 * `GET /api/cases/:caseId`) and rendered as returned. Every response is
 * per-user (`unread`), which is why the server marks them
 * `Cache-Control: no-store`; React Query's own cache is per tab and is
 * cleared on sign-out.
 *
 * Query keys carry the full view/filter/search tuple, so two lists that
 * differ in any part of it never share a cache entry, and everything hangs
 * under the `["cases", …]` root that {@link invalidateCaseData} in
 * `caseMutations.ts` marks stale after any Parts action.
 */

// ---------------------------------------------------------------------------
// Parameters and keys
// ---------------------------------------------------------------------------

export type CaseListParams = Readonly<{
  view: CaseListView;
  /** Free-text search applied inside the view; blank means no filter. */
  query?: string;
  /** Only cases with a part line that has no ETA. */
  noEta?: boolean;
  /** Only cases that have no RO yet. */
  preRo?: boolean;
  /** Page size, 1–100; defaults to {@link LIST_PAGE_SIZE}. */
  limit?: number;
}>;

export type CaseDetailOptions = Readonly<{
  activityCursor?: string;
  activityLimit?: number;
}>;

/** The list key's stable payload: every part of the tuple, normalised. */
export type CaseListKeyParams = Readonly<{
  view: CaseListView;
  q: string;
  noEta: boolean;
  preRo: boolean;
}>;

/** A list or search page as the infinite queries hold it. */
export type InfinitePages<T> = InfiniteData<CursorPage<T>, string | null>;

export function normalizeListParams(params: CaseListParams): CaseListKeyParams {
  return {
    view: params.view,
    q: (params.query ?? "").trim(),
    noEta: params.noEta ?? false,
    preRo: params.preRo ?? false,
  };
}

export const caseQueryKeys = {
  /** Root of every case query — invalidate it after any case mutation. */
  all: () => ["cases"] as const,
  lists: () => ["cases", "list"] as const,
  list: (params: CaseListParams) =>
    ["cases", "list", normalizeListParams(params)] as const,
  counts: () => ["cases", "counts"] as const,
  details: () => ["cases", "detail"] as const,
  detail: (caseId: string) => ["cases", "detail", caseId] as const,
  /**
   * Older activity pages, nested under the detail and keyed by the cursor the
   * detail's first page ended on.
   */
  olderActivity: (caseId: string, fromCursor: string | null) =>
    ["cases", "detail", caseId, "older-activity", { from: fromCursor }] as const,
  searches: () => ["cases", "search"] as const,
  search: (query: string) => ["cases", "search", query.trim()] as const,
} as const;

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** Default page size; the server's own default and maximum live in the contract. */
export const LIST_PAGE_SIZE = 40;

/**
 * `GET /api/cases?view=…&query=…&noEta=true&preRo=true&cursor=…&limit=…`.
 * Flags travel only when on (the server reads an absent flag as off) and the
 * search only when non-blank, so the request is as short as the URL state.
 */
export function caseListPath(
  params: CaseListParams,
  cursor?: string | null,
  limit: number = params.limit ?? LIST_PAGE_SIZE,
): string {
  const normalized = normalizeListParams(params);
  const search = new URLSearchParams();
  search.set("view", normalized.view);
  if (normalized.q) search.set("query", normalized.q);
  if (normalized.noEta) search.set("noEta", "true");
  if (normalized.preRo) search.set("preRo", "true");
  if (cursor) search.set("cursor", cursor);
  search.set("limit", String(limit));
  return `/api/cases?${search.toString()}`;
}

/** `GET /api/search?query=…&cursor=…&limit=…` — the server requires 1–120 characters after trimming. */
export function searchPath(
  query: string,
  cursor?: string | null,
  limit?: number,
): string {
  const search = new URLSearchParams();
  search.set("query", query.trim());
  if (limit !== undefined) search.set("limit", String(limit));
  if (cursor) search.set("cursor", cursor);
  return `/api/search?${search.toString()}`;
}

export const caseReadPaths = {
  counts: () => "/api/cases/counts",
  /** The detail route, optionally asked for a specific page of the activity timeline. */
  detail: (caseId: string, options: CaseDetailOptions = {}) => {
    const search = new URLSearchParams();
    if (options.activityCursor) search.set("activityCursor", options.activityCursor);
    if (options.activityLimit !== undefined) {
      search.set("activityLimit", String(options.activityLimit));
    }
    const encoded = search.toString();
    const base = `/api/cases/${encodeURIComponent(caseId)}`;
    return encoded ? `${base}?${encoded}` : base;
  },
  /** The same detail route, asked for the page of older activity that starts at `cursor`. */
  activityPage: (caseId: string, cursor: string) =>
    `/api/cases/${encodeURIComponent(caseId)}?activityCursor=${encodeURIComponent(cursor)}`,
  markViewed: (caseId: string) =>
    `/api/cases/${encodeURIComponent(caseId)}/read`,
} as const;

/** The read paths under one name (list, detail, search). */
export const queryPaths = {
  list: (params: CaseListParams, cursor?: string | null) =>
    caseListPath(params, cursor),
  detail: (caseId: string, options: CaseDetailOptions = {}) =>
    caseReadPaths.detail(caseId, options),
  search: (query: string, cursor?: string | null, limit?: number) =>
    searchPath(query, cursor, limit),
} as const;

// ---------------------------------------------------------------------------
// Request functions
// ---------------------------------------------------------------------------

export function fetchCaseList(
  params: CaseListParams,
  cursor?: string | null,
): Promise<CursorPage<CaseListItem>> {
  return request<CursorPage<CaseListItem>>(caseListPath(params, cursor));
}

export function fetchCaseCounts(): Promise<CaseCounts> {
  return request<CaseCounts>(caseReadPaths.counts());
}

export function fetchCaseDetail(
  caseId: string,
  options: CaseDetailOptions = {},
): Promise<CaseDetail> {
  return request<CaseDetail>(caseReadPaths.detail(caseId, options));
}

export function fetchSearch(
  query: string,
  cursor?: string | null,
  limit?: number,
): Promise<CursorPage<SearchHit>> {
  return request<CursorPage<SearchHit>>(searchPath(query, cursor, limit));
}

/** One page of older activity events (newest first), as the detail route returns it. */
export async function fetchCaseActivityPage(
  caseId: string,
  cursor: string,
): Promise<CursorPage<ActivityEventView>> {
  const detail = await request<CaseDetail>(
    caseReadPaths.activityPage(caseId, cursor),
  );
  return detail.activity;
}

/**
 * `POST /api/cases/:caseId/read` with exactly the
 * `readThroughActivityEventId` a detail response carried. The server checks
 * the event belongs to the case and never moves the marker backwards.
 */
export async function markCaseViewed(
  caseId: string,
  input: MarkReadInput,
): Promise<void> {
  await request<unknown>(caseReadPaths.markViewed(caseId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export type CaseListPages = InfinitePages<CaseListItem>;

/**
 * The cursor-paginated list for one view/filter/search tuple. Pages append
 * through `fetchNextPage`; while the tuple changes (typing a search) the
 * previous rows stay on screen as placeholder data so the pane does not
 * flash empty between keystrokes.
 */
export function useCaseList(
  params: CaseListParams,
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<CaseListPages> {
  return useInfiniteQuery({
    queryKey: caseQueryKeys.list(params),
    queryFn: ({ pageParam }) => fetchCaseList(params, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** Every item across the pages loaded so far, in server order. */
export function flattenPages<T>(data: InfinitePages<T> | undefined): T[] {
  if (!data) return [];
  return data.pages.flatMap((page) => page.items);
}

/** Every row loaded so far, in server order. */
export function flattenCasePages(
  data: CaseListPages | undefined,
): readonly CaseListItem[] {
  return flattenPages(data);
}

export function useCaseCounts(): UseQueryResult<CaseCounts> {
  return useQuery({
    queryKey: caseQueryKeys.counts(),
    queryFn: fetchCaseCounts,
  });
}

/** The full case detail; disabled while no case is selected. */
export function useCaseDetail(
  caseId: string | null,
): UseQueryResult<CaseDetail> {
  return useQuery({
    queryKey: caseQueryKeys.detail(caseId ?? ""),
    queryFn: () => fetchCaseDetail(caseId ?? ""),
    enabled: caseId !== null && caseId.length > 0,
  });
}

/**
 * Global search across active and historical cases (spec §12). Disabled for a
 * blank query — the server requires 1–120 characters after trimming.
 */
export function useCaseSearch(
  query: string,
): UseInfiniteQueryResult<InfinitePages<SearchHit>> {
  const trimmed = query.trim();
  return useInfiniteQuery({
    queryKey: caseQueryKeys.search(trimmed),
    queryFn: ({ pageParam }) => fetchSearch(trimmed, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: trimmed.length > 0,
  });
}

export type ActivityPages = InfinitePages<ActivityEventView>;

/**
 * Pages of activity older than the detail's first page. Nothing loads until
 * the person asks (`enabled`), then each `fetchNextPage` follows the cursor
 * the previous page ended on. The timeline collapses older history; it never
 * discards it.
 */
export function useOlderActivity(
  caseId: string | null,
  fromCursor: string | null,
  enabled: boolean,
): UseInfiniteQueryResult<ActivityPages> {
  return useInfiniteQuery({
    queryKey: caseQueryKeys.olderActivity(caseId ?? "", fromCursor),
    queryFn: ({ pageParam }) =>
      fetchCaseActivityPage(caseId ?? "", pageParam ?? ""),
    initialPageParam: fromCursor,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && caseId !== null && fromCursor !== null,
  });
}

export type MarkViewedVariables = Readonly<{
  caseId: string;
  throughActivityEventId: string;
}>;

/**
 * Acknowledges a case as viewed through the server's own read cursor.
 * Success marks list queries stale so the unread indicator clears on the
 * next render; the detail itself is left alone (its content did not change,
 * and refetching it would only hand back the same cursor).
 */
export function useMarkCaseViewed(): UseMutationResult<
  void,
  unknown,
  MarkViewedVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, throughActivityEventId }: MarkViewedVariables) =>
      markCaseViewed(caseId, { throughActivityEventId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "cases" && query.queryKey[1] === "list",
      }),
  });
}
